import { useCallback, useEffect, useState } from "react";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const FRESH = {
  none: { label: "No Session", tone: "bg-slate-100 text-slate-500", icon: "Circle" },
  current: { label: "Current", tone: "bg-emerald-100 text-emerald-700", icon: "CheckCircle2" },
  new_documents_available: { label: "New Documents Available", tone: "bg-amber-100 text-amber-700", icon: "AlertTriangle" },
};
const SEV = { info: "text-slate-500", attention: "text-amber-600", warning: "text-red-600" };
const CONF = { high: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", low: "bg-slate-100 text-slate-500" };

function Count({ label, value, tone }) {
  return (
    <div className="rounded-lg border border-slate-100 bg-white px-3 py-2 text-center" data-testid={`pkgintel-count-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className={`text-lg font-semibold ${tone}`}>{value ?? 0}</div>
      <div className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</div>
    </div>
  );
}

/** Phase 2D-ii — Package Intelligence: cross-document reasoning over the client's immutable DIPs.
 * Staff-triggered Intelligence Session (frozen snapshot); drill down to the full session. */
export default function PackageIntelligencePanel({ clientId }) {
  const [fresh, setFresh] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [session, setSession] = useState(null);

  const load = useCallback(() => {
    api.get(`/documents-intel/package/${clientId}/status`).then((r) => setFresh(r.data)).catch(() => {});
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const run = async (force) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/documents-intel/package/${clientId}/run`, { force });
      if (data.skipped === "no_changes") toast.message("No changes detected since the last Intelligence Session.");
      else if (data.skipped) toast.message(data.message || "Nothing to reason over yet.");
      else { toast.success(`Intelligence Session #${data.session.session_number} generated`); }
      load();
    } catch (e) { toast.error("Package Intelligence failed"); }
    finally { setBusy(false); }
  };

  const openSession = async () => {
    const sid = fresh?.session?.id;
    if (!sid) return;
    try { const { data } = await api.get(`/documents-intel/package/sessions/${sid}`); setSession(data.session); setOpen(true); }
    catch { toast.error("Could not load session"); }
  };

  const confirmPtf = async (pid) => {
    try { const { data } = await api.post(`/documents-intel/package/sessions/${session.id}/ptf/${pid}/confirm`); toast.success(`Permanent Tax File: ${data.outcome}`); openSession(); }
    catch (e) { toast.error(e.response?.data?.detail || "Confirm failed"); }
  };
  const dismissPtf = async (pid) => {
    try { await api.post(`/documents-intel/package/sessions/${session.id}/ptf/${pid}/dismiss`, {}); toast.message("Proposal dismissed"); openSession(); }
    catch { toast.error("Failed"); }
  };

  const st = FRESH[fresh?.status] || FRESH.none;
  const StIcon = Icons[st.icon] || Icons.Circle;
  const c = fresh?.session?.counts || {};
  const hasSession = !!fresh?.session;

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 mb-5" data-testid="package-intelligence-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icons.BrainCircuit className="h-4 w-4 text-violet-600" />
          <span className="text-sm font-semibold text-slate-800">Package Intelligence</span>
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1 ${st.tone}`} data-testid="pkgintel-status">
            <StIcon className="h-3 w-3" />{st.label}
          </span>
          {fresh?.status === "new_documents_available" && fresh?.new_documents_since_session > 0 && (
            <span className="text-[10px] text-amber-600" data-testid="pkgintel-newdocs">{fresh.new_documents_since_session} new since session</span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {hasSession && (
            <button onClick={openSession} data-testid="pkgintel-view" className="text-[11px] border border-slate-300 rounded-lg px-3 py-1.5 font-medium hover:bg-white">View Full Session</button>
          )}
          <button onClick={() => run(false)} disabled={busy} data-testid="pkgintel-run" className="text-[11px] bg-violet-600 text-white rounded-lg px-3 py-1.5 font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
            {busy ? <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Icons.Sparkles className="h-3.5 w-3.5" />}
            {hasSession ? "Re-run" : "Run Package Intelligence"}
          </button>
        </div>
      </div>

      {!hasSession && (
        <div className="text-xs text-slate-500" data-testid="pkgintel-empty">
          Run Package Intelligence to have Allison reason across this client's AI-Ready, classified documents ({fresh?.live_document_count ?? 0} ready) — a frozen, evidence-backed Intelligence Session.
        </div>
      )}

      {hasSession && (
        <>
          <div className="text-xs text-slate-600 mb-3" data-testid="pkgintel-summary">{fresh.session.summary}</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
            <Count label="Confirmed Facts" value={c.confirmed_facts} tone="text-emerald-600" />
            <Count label="Contradictions" value={c.contradictions} tone="text-red-600" />
            <Count label="Evidence Gaps" value={c.evidence_gaps} tone="text-amber-600" />
            <Count label="Duplicates" value={c.duplicates} tone="text-slate-600" />
            <Count label="PTF Proposals" value={c.ptf_proposals} tone="text-blue-600" />
          </div>
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="pkgintel-session-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Icons.BrainCircuit className="h-4 w-4 text-violet-600" />Intelligence Session #{session?.session_number}</DialogTitle></DialogHeader>
          {session && (
            <div className="space-y-4 text-sm">
              <div className="text-slate-600" data-testid="session-summary">{session.summary}</div>
              <div className="text-[11px] text-slate-400">
                {session.dips_consumed?.length} document(s) · {session.model_used} · Taxonomy v{session.taxonomy_version} · {new Date(session.created_at).toLocaleString()}
              </div>

              <Section title="Contradictions" icon="GitCompareArrows" items={session.contradictions} testid="session-contradictions"
                render={(x) => (<><div className="text-slate-700">{x.description}</div>
                  {x.recommendation && <div className="text-[11px] text-slate-500 mt-0.5">→ {x.recommendation}</div>}
                  <SrcDips dips={x.source_dips} /><BasisConf x={x} /></>)} />

              <Section title="Evidence Gaps" icon="FileSearch" items={session.evidence_gaps} testid="session-gaps"
                render={(x) => (<><div className="text-slate-700">{x.description}</div>
                  {x.recommendation && <div className="text-[11px] text-slate-500 mt-0.5">→ {x.recommendation}</div>}<BasisConf x={x} /></>)} />

              <Section title="Duplicate Evidence" icon="Copy" items={session.duplicates} testid="session-duplicates"
                render={(x) => (<><div className="text-slate-700">{x.description}</div><SrcDips dips={x.source_dips} /><BasisConf x={x} /></>)} />

              <div data-testid="session-ptf">
                <SectionHead icon="FileStack" title="Permanent Tax File Proposals" n={session.ptf_proposals?.length} />
                {(session.ptf_proposals || []).length === 0 && <div className="text-xs text-slate-400">None proposed.</div>}
                {(session.ptf_proposals || []).map((p) => (
                  <div key={p.id} className="rounded-lg border border-slate-100 p-2 mb-1.5" data-testid={`session-ptf-${p.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-slate-800 font-medium">{p.field}: {p.value}</span>
                      <span className={`text-[9px] px-1 rounded ${CONF[p.confidence] || "bg-slate-100"}`}>{p.confidence}</span>
                      {p.disposition && <span className="text-[10px] text-emerald-600 ml-auto capitalize">{p.disposition}{p.outcome ? ` · ${p.outcome}` : ""}</span>}
                    </div>
                    {p.reason && <div className="text-[11px] text-slate-500 mt-0.5">{p.reason}</div>}
                    <SrcDips dips={p.source_dips} />
                    {!p.disposition && (
                      <div className="flex gap-1.5 mt-1.5">
                        <button onClick={() => confirmPtf(p.id)} data-testid={`session-ptf-confirm-${p.id}`} className="text-[10px] bg-emerald-600 text-white rounded px-2 py-1">Confirm to PTF</button>
                        <button onClick={() => dismissPtf(p.id)} data-testid={`session-ptf-dismiss-${p.id}`} className="text-[10px] border border-slate-200 rounded px-2 py-1">Dismiss</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <Section title="Package Observations" icon="Eye" items={session.package_observations} testid="session-observations"
                render={(x) => (<div className="flex items-start gap-1.5"><Icons.Dot className={`h-4 w-4 shrink-0 ${SEV[x.severity] || SEV.info}`} /><span className="text-slate-700">{x.text}</span></div>)} />

              {session.decision_trace && (
                <div className="text-[10px] text-slate-500 rounded-lg bg-slate-50 border border-slate-100 px-2.5 py-2 space-y-0.5" data-testid="session-trace">
                  <div className="font-semibold text-slate-600">{session.decision_trace.engine} · {session.decision_trace.status}</div>
                  {(session.decision_trace.applicable_policies || []).map((p, i) => <div key={i}>• {p}</div>)}
                  <div className="text-slate-400">Inputs: {(session.decision_trace.inputs_consumed || []).join(", ")}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const SectionHead = ({ icon, title, n }) => {
  const Ic = Icons[icon] || Icons.Circle;
  return <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1.5 flex items-center gap-1.5"><Ic className="h-3.5 w-3.5" />{title} {n != null && <span className="text-slate-300">({n})</span>}</div>;
};
const Section = ({ title, icon, items, render, testid }) => (
  <div data-testid={testid}>
    <SectionHead icon={icon} title={title} n={items?.length} />
    {(!items || items.length === 0) && <div className="text-xs text-slate-400">None found.</div>}
    {(items || []).map((x) => <div key={x.id} className="rounded-lg border border-slate-100 p-2 mb-1.5 text-[12px]">{render(x)}</div>)}
  </div>
);
const SrcDips = ({ dips }) => (dips && dips.length ? <div className="flex flex-wrap gap-1 mt-1">{dips.map((d, i) => <span key={i} className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{d.title || d.document_id}{d.value ? `: ${d.value}` : ""}</span>)}</div> : null);
const BasisConf = ({ x }) => (<div className="flex gap-1 mt-1">{x.basis && <span className="text-[9px] px-1 rounded bg-violet-50 text-violet-600">{x.basis}</span>}{x.confidence && <span className={`text-[9px] px-1 rounded ${CONF[x.confidence] || "bg-slate-100"}`}>{x.confidence}</span>}</div>);
